// Phones (iPhone by default) save photos as HEIC/HEIF, which Chrome and Windows
// can't display. Convert them to JPEG before upload so the stored answer file is
// viewable everywhere. Any failure falls back to uploading the original file.

const HEIC2ANY_URL = 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js';
const JPEG_QUALITY = 0.9;

let heic2anyPromise = null;

export function isHeic(file) {
    const type = (file.type || '').toLowerCase();
    const extension = file.name.toLowerCase().split('.').pop();

    return type.startsWith('image/heic') || type.startsWith('image/heif') || ['heic', 'heif'].includes(extension);
}

// Safari 17+ (current iPhones) decodes HEIC natively; other browsers reject here.
async function convertNatively(file) {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext('2d').drawImage(bitmap, 0, 0);
    bitmap.close();

    return new Promise((resolve, reject) => {
        canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('toBlob failed')), 'image/jpeg', JPEG_QUALITY);
    });
}

// ~2.7MB decoder, so it's fetched only the first time a browser can't decode HEIC itself.
function loadHeic2any() {
    if (window.heic2any) {
        return Promise.resolve(window.heic2any);
    }

    if (!heic2anyPromise) {
        heic2anyPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = HEIC2ANY_URL;
            script.onload = () => resolve(window.heic2any);
            script.onerror = () => {
                heic2anyPromise = null;
                reject(new Error('heic2any failed to load'));
            };
            document.head.appendChild(script);
        });
    }

    return heic2anyPromise;
}

async function convertWithLibrary(file) {
    const heic2any = await loadHeic2any();
    const result = await heic2any({blob: file, toType: 'image/jpeg', quality: JPEG_QUALITY});

    // Live Photos / bursts decode to several frames; keep the first.
    return Array.isArray(result) ? result[0] : result;
}

export async function heicToJpeg(file) {
    if (!isHeic(file)) {
        return file;
    }

    let blob = null;
    try {
        blob = await convertNatively(file);
    } catch (e) {
        try {
            blob = await convertWithLibrary(file);
        } catch (e) {
            return file;
        }
    }

    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';

    return new File([blob], name, {type: 'image/jpeg', lastModified: file.lastModified});
}
